import { daysBetween, type PlainDate } from './date';
import { PEOPLE_ADMIN_ROLES, type Role } from './enums';

/**
 * The "Get started" checklist: which steps a person gets, in what order, and when
 * the worker reminds them about the ones still open.
 *
 * Shared because both sides ask. The dashboard card renders the steps and the worker
 * reminds people about the same ones; if each kept its own list, the bell would one
 * day name a step the card no longer shows. Each side works out whether a step is
 * done from its own data - what the steps ARE, and who gets them, lives only here.
 */
export type OnboardingStep =
  | 'PEOPLE_SET_UP'
  | 'KRA_GROUP'
  | 'TEAM_GOALS'
  | 'ONE_ON_ONE'
  | 'DEVELOPMENT_GOAL'
  | 'LOG_PROGRESS'
  | 'RECOGNITION';

export const ONBOARDING_STEPS: Record<OnboardingStep, { title: string; href: string }> = {
  PEOPLE_SET_UP: { title: 'Finish setting up your people', href: '/hr/employees' },
  KRA_GROUP: { title: 'Create a KRA group', href: '/hr/kra-groups' },
  TEAM_GOALS: { title: 'Publish your team’s goals', href: '/team/goals' },
  ONE_ON_ONE: { title: 'Schedule a 1-on-1', href: '/team/meetings' },
  DEVELOPMENT_GOAL: { title: 'Add a development goal', href: '/goals' },
  LOG_PROGRESS: { title: 'Log progress on a KRA', href: '/assessments' },
  RECOGNITION: { title: 'Recognise a colleague', href: '/recognition' },
};

/**
 * HR setup first, then the team, then your own. Team steps key on having reports,
 * never on the MANAGER role - same as the sidebar's "My team".
 */
export function onboardingStepsFor(ctx: { role: Role; hasReports: boolean }): OnboardingStep[] {
  return [
    ...(PEOPLE_ADMIN_ROLES.includes(ctx.role) ? (['PEOPLE_SET_UP', 'KRA_GROUP'] as const) : []),
    ...(ctx.hasReports ? (['TEAM_GOALS', 'ONE_ON_ONE'] as const) : []),
    'DEVELOPMENT_GOAL',
    'LOG_PROGRESS',
    'RECOGNITION',
  ];
}

/**
 * Days after the first reminder on which each one is due: day 1, 3, 7 and 14, then
 * never again. A reminder that never stops is one people filter; past two weeks the
 * card on the dashboard is still there for anyone who wants it.
 */
export const ONBOARDING_REMINDER_OFFSETS = [0, 2, 6, 13] as const;

/**
 * Is the next reminder due on `on`? `sent` is the days earlier reminders went out,
 * oldest first. At most one a day, even when catching up after the worker was down.
 */
export function onboardingReminderDue(sent: readonly PlainDate[], on: PlainDate): boolean {
  const first = sent[0];
  const last = sent[sent.length - 1];
  if (first === undefined || last === undefined) return true;
  const offset = ONBOARDING_REMINDER_OFFSETS[sent.length];
  return offset !== undefined && last < on && daysBetween(first, on) >= offset;
}
